import './style.scss';

import type { ProgressProps } from 'antd';
import { Progress } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';

import { ApiPairs } from 'api';
import { Job } from 'api/apiPairs/models';
import { PairWalletsContext } from 'context/PairWalletsContext/PairWalletsContext';
import { JOB_ID_CACHE_KEY } from 'shared/consts';
import { useTypedSelector } from 'store';
import { LocalStorage } from 'tools';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { Checkbox } from 'ui/Checkbox/Checkbox';
import { Modal } from 'ui/Modal/Modal';

type WalletSyncProps = {
  onClose: () => void;
};

const progressColors: ProgressProps['strokeColor'] = {
  '0%': '#af8cf6',
  '100%': '#7553bb',
};

export const WalletSync = ({ onClose }: WalletSyncProps) => {
  const storageJobId = LocalStorage.get(JOB_ID_CACHE_KEY);
  const { handleRemount } = useContext(PairWalletsContext);

  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;

  const [isCheckedDryRun, setIsCheckedDryRun] = useState(true);
  const [jobData, setJobData] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleGetJob = async (jobId: string) => {
    setIsLoading(true);

    try {
      const response = await ApiPairs.getBackgroundJob({ jobId });

      if (response.data) {
        setJobData(response.data);

        if (response.data.progress === 100) {
          setIsLoading(false);
          LocalStorage.remove(JOB_ID_CACHE_KEY);
          handleRemount();
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to get job data:', error);
    }
  };

  const handleStartSync = async () => {
    if (!dexPair) return;

    const response = await ApiPairs.syncWallets({
      pairId: dexPair.id,
      dryRun: isCheckedDryRun,
    });

    if (response.data) {
      const jobId = response.data.job_id;
      LocalStorage.set(JOB_ID_CACHE_KEY, jobId);
      handleGetJob(jobId);
    }
  };

  useEffect(() => {
    const jobId = typeof storageJobId === 'string' ? storageJobId : null;

    if (!jobId) return;

    handleGetJob(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const jobId = typeof storageJobId === 'string' ? storageJobId : null;

    if (!jobId) return;

    const interval = setInterval(() => {
      if (jobData?.progress !== 100) {
        handleGetJob(jobId);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [jobData, storageJobId]);

  const progress = useMemo(() => jobData?.progress, [jobData]);

  return (
    <Modal title="Wallet Sync" onClose={onClose}>
      <div className="ui-wallet-sync">
        {(!jobData || progress === 100) && (
          <div className="flex items-center gap-3">
            <Checkbox
              checked={isCheckedDryRun}
              onChange={() => setIsCheckedDryRun(prev => !prev)}
            />
            <span>Only checking balances, no changes</span>
          </div>
        )}

        {jobData && (
          <div className="flex flex-col gap-2">
            {progress === 100 ? (
              <span>Synchronization completed</span>
            ) : (
              <span>The process of synchronizing wallets is in progress</span>
            )}
            <Progress
              percent={jobData.progress}
              percentPosition={{ align: 'center', type: 'inner' }}
              size={[440, 30]}
              trailColor="#292932"
              strokeColor={progressColors}
            />

            {jobData.data && (
              <div className="flex flex-col gap-2 mt-4">
                <div className="ui-wallet-sync__stat">
                  <span>Total number of wallets:</span>
                  <strong>{jobData.data.total_cnt}</strong>
                </div>

                <div className="ui-wallet-sync__stat">
                  <span>Number of wallets with synchronization error:</span>
                  <strong>{jobData.data.err_cnt}</strong>
                </div>

                <div className="ui-wallet-sync__stat">
                  <span>Number of wallets that were synchronized:</span>
                  <strong>{jobData.data.upd_cnt}</strong>
                </div>

                <div className="ui-wallet-sync__stat">
                  <span>Number of virtual deposits that were created:</span>
                  <strong>{jobData.data.deposits_cnt}</strong>
                </div>

                <div className="ui-wallet-sync__stat">
                  <span>Number of virtual withdraws that were created:</span>
                  <strong>{jobData.data.withdraws_cnt}</strong>
                </div>
              </div>
            )}
          </div>
        )}

        {!isLoading ? (
          <Button onClick={handleStartSync}>Start syncing wallets</Button>
        ) : (
          <ButtonLoading />
        )}
      </div>
    </Modal>
  );
};
